import { useState } from "react";
import { CheckCircle2, AlertTriangle, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "aldri";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 60) return "nå";
  if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} t siden`;
  return `${Math.floor(diff / 86400)} d siden`;
}

/**
 * Reusable card for OAuth-based calendar integrations (Google, Outlook).
 * Apple iCloud uses a different flow (form input) so it's rendered inline.
 */
export default function OAuthIntegrationCard({
  label, icon: Icon, accentClass, testid,
  status, getAuthorizeUrl, syncNow, disconnect, onChanged,
}) {
  const [busy, setBusy] = useState(false);

  if (!status?.configured) {
    return (
      <section className={`rounded-xl border ${accentClass.border} ${accentClass.bg} p-4`} data-testid={testid}>
        <h3 className="text-sm font-semibold text-stone-800 mb-1 flex items-center gap-2">
          <Icon className={`h-4 w-4 ${accentClass.icon}`} /> {label}
        </h3>
        <p className="text-xs text-stone-500">
          Ikke konfigurert på serveren. Administrator må legge inn OAuth-nøkler i backend/.env.
        </p>
      </section>
    );
  }

  const connect = async () => {
    setBusy(true);
    try {
      const res = await getAuthorizeUrl();
      const url = res.data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke starte oppsett");
      setBusy(false);
    }
  };

  const handleSync = async () => {
    setBusy(true);
    try {
      const res = await syncNow();
      toast.success(`Synket · ${res.data?.pulled || 0} hentet, ${res.data?.pushed || 0} sendt`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Synk feilet");
    } finally { setBusy(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Koble fra ${label}? Allerede importerte avtaler beholdes, men auto-synk stopper.`)) return;
    setBusy(true);
    try {
      await disconnect();
      toast.success(`${label} frakoblet`);
      onChanged?.();
    } catch { toast.error("Kunne ikke koble fra"); }
    finally { setBusy(false); }
  };

  return (
    <section className={`rounded-xl border ${accentClass.border} ${accentClass.bg} p-4`} data-testid={testid}>
      <h3 className="text-sm font-semibold text-stone-800 mb-1 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${accentClass.icon}`} /> {label}
      </h3>

      {!status.connected && (
        <>
          <p className="text-xs text-stone-600 mb-3">
            Koble til for toveis-sync. Du logger inn med kontoen din og godkjenner tilgang til kalenderen. Auto-synk kjører hvert 15. min.
          </p>
          <button
            onClick={connect}
            disabled={busy}
            className={`w-full inline-flex items-center justify-center gap-2 rounded-md ${accentClass.btn} px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50`}
            data-testid={`${testid}-connect-btn`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {busy ? "Omdirigerer…" : `Koble til ${label}`}
          </button>
        </>
      )}

      {status.connected && (
        <div className="space-y-2">
          <div className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="h-4 w-4 text-emerald-700 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-900 truncate">{status.email || label}</p>
                <p className="text-[11px] text-stone-500">Sist synk: {relTime(status.last_sync_at)}</p>
              </div>
            </div>
            <div className="text-right text-[11px] text-stone-600 flex-shrink-0">
              <p>{status.last_pulled ?? 0} hentet</p>
              <p>{status.last_pushed ?? 0} sendt</p>
            </div>
          </div>
          {status.last_error && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-2 text-[11px] text-rose-800 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> {status.last_error}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleSync}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-2 text-xs font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
              data-testid={`${testid}-sync-btn`}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Synk nå
            </button>
            <button
              onClick={handleDisconnect}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              data-testid={`${testid}-disconnect-btn`}
            >
              <Trash2 className="h-3.5 w-3.5" /> Koble fra
            </button>
          </div>
          <p className="text-[11px] text-stone-400 text-center">Auto-synk hvert 15. min</p>
        </div>
      )}
    </section>
  );
}
